import { BaseApiService } from './baseApi';
import type { ProjectBackend as Project, ApiResponse } from '../../../../types';

export interface ProjectsFilter {
  academic?: boolean;
  professional?: boolean;
  search?: string;
  technology?: string;
  withLinks?: boolean;
  withFeatures?: boolean;
  visible?: boolean;
  limit?: number;
  offset?: number;
  orderBy?: 'created_at' | 'updated_at' | 'title' | 'display_order';
  orderDirection?: 'asc' | 'desc';
}

export interface CreateProjectRequest {
  title: string;
  description: string;
  technologies: string[];
  is_academic: boolean;
  badge?: string;
  live_url?: string;
  github_url?: string;
  image_url?: string;
  show_live_url?: boolean;
  show_github_url?: boolean;
}

export class ProjectsApiService extends BaseApiService {
  constructor() {
    super('/projects');
  }

  /**
   * Fetch tous les projets avec filtres
   */
  async getAll(filters?: ProjectsFilter): Promise<ApiResponse<Project[]>> {
    const queryString = filters ? this.buildQueryString(filters) : '';
    return this.requestGet<Project[]>(`${queryString}`, false);
  }

  /**
   * Fetch les projets académiques
   */
  async getAcademic(): Promise<ApiResponse<Project[]>> {
    return this.getAll({ academic: true });
  }

  /**
   * Fetch les projets professionnels
   */
  async getProfessional(): Promise<ApiResponse<Project[]>> {
    return this.getAll({ academic: false });
  }

  /**
   * Fetch un projet par ID
   */
  async getById(id: string): Promise<ApiResponse<Project>> {
    return this.requestGet<Project>(`/${id}`, false);
  }

  /**
   * Create un nouveau projet
   */
  async create(data: CreateProjectRequest): Promise<ApiResponse<Project>> {
    return this.requestPost<Project>('', data);
  }

  /**
   * Update un projet
   */
  async update(id: string, data: Partial<CreateProjectRequest>): Promise<ApiResponse<Project>> {
    return this.requestPut<Project>(`/${id}`, data);
  }

  /**
   * Delete un projet
   * ✅ Correction : Pas de conflit avec BaseApiService grâce au préfixe 'request'
   */
  async delete(id: string): Promise<ApiResponse<boolean>> {
    return this.requestDelete<boolean>(`/${id}`);
  }

  /**
   * Réorganiser les projets
   */
  async reorder(orderedIds: string[]): Promise<ApiResponse<Project[]>> {
    return this.requestPost<Project[]>('/reorder', { orderedIds });
  }

  /**
   * Toggle la visibilité d'un projet
   */
  async toggleVisibility(id: string): Promise<ApiResponse<Project>> {
    return this.requestPatch<Project>(`/${id}/toggle-visibility`);
  }

  /**
   * Search des projets
   */
  async search(query: string): Promise<ApiResponse<Project[]>> {
    return this.requestGet<Project[]>(`/search${this.buildQueryString({ q: query })}`, false);
  }
}

export const projectsApi = new ProjectsApiService();
